'use client';

import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LockIcon, CalendarIcon, ImageIcon } from 'lucide-react';

import { db } from '@/lib/firebase.client';
import { doc, getDoc } from 'firebase/firestore';
import EventLightbox from '@/components/event-lightbox';
import { getLocalizedPathname, parseContentSegment } from '@/lib/site';
import type { LocaleCode } from '@/types/sections';

type EventType = 'evenement public' | 'evenement privee';
type GalleryContentItem = { id?: string|number; image: string; title?: string; description?: string };
type MediaItem = { id?: string|number; type: 'image'|'video'; url: string; title?: string; desc?: string; span?: string };

type GalleryDoc = {
  title: string;
  description: string;
  date: string;
  cover: string;
  type: EventType;
  accessKey?: string;
  content?: GalleryContentItem[];
  media?: MediaItem[];
  cards?: {
    id?: string;
    image: string;
    title: string;
    description: string;
  }[];
  isPublished?: boolean;
};

function canAccessPrivate(evt: GalleryDoc, key?: string) {
  if (evt.type !== 'evenement privee') return true;
  const k = (key || '').trim();
  if (!k) return false;
  if (evt.accessKey && k === evt.accessKey) return true;
  // Legacy fallback if you used title as key earlier
  return k === evt.title;
}

export default function EventDetailPage() {
  const params = useParams<{ id: string }>();
  const searchParams = useSearchParams();
  const router = useRouter();
  const locale = (useLocale() as LocaleCode) ?? 'en';

  const [evt, setEvt] = React.useState<(GalleryDoc & { id: string }) | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [storedKey, setStoredKey] = React.useState<string | null>(null);
  const [keyInput, setKeyInput] = React.useState('');
  const [unlockError, setUnlockError] = React.useState('');

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const ref = doc(db, 'website-gallery', parseContentSegment(params.id).id);
        const snap = await getDoc(ref);
        if (!active) return;

        if (!snap.exists()) {
          router.replace(getLocalizedPathname(locale, '/gallery'));
          return;
        }
        const data = { id: snap.id, ...(snap.data()) } as GalleryDoc & { id: string };

        // Hide unpublished if you use that flag
        if (data.isPublished === false) {
          router.replace(getLocalizedPathname(locale, '/gallery'));
          return;
        }

        setEvt(data);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [locale, params.id, router]);

  React.useEffect(() => {
    if (!evt) return;
    if (typeof window === 'undefined') return;
    try {
      const saved = window.localStorage.getItem(`gallery-access-${evt.id}`);
      if (saved) {
        setStoredKey(saved);
      }
    } catch {
      // ignore storage errors
    }
  }, [evt]);

  React.useEffect(() => {
    if (!evt) return;
    const keyFromUrl = searchParams.get('key');
    if (!keyFromUrl) return;
    if (canAccessPrivate(evt, keyFromUrl)) {
      try {
        window.localStorage.setItem(`gallery-access-${evt.id}`, keyFromUrl);
      } catch {
        // ignore storage errors
      }
      setStoredKey(keyFromUrl);
    }
  }, [evt, searchParams]);

  if (loading) {
    return <div className="min-h-[60vh] w-full">
      <div className="h-[55vh] w-full animate-pulse bg-muted" />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="h-6 w-2/3 bg-muted animate-pulse rounded mb-4" />
        <div className="h-4 w-1/2 bg-muted animate-pulse rounded" />
      </div>
    </div>;
  }

  if (!evt) return <div className="min-h-[40vh]" />;

  const isPrivate = evt.type === 'evenement privee';
  const hasAccess = canAccessPrivate(evt, storedKey || undefined);

  const photos = extractPhotos(evt);
  const images = photos.map((photo) => ({
    src: photo.image,
    alt: photo.title || evt.title,
    title: photo.title,
    desc: photo.description,
  }));

  const submitKey = (e: React.FormEvent) => {
    e.preventDefault();
    if (!evt) return;

    const trimmed = keyInput.trim();
    if (!trimmed) {
      setUnlockError('Merci de saisir la clé fournie.');
      return;
    }

    if (canAccessPrivate(evt, trimmed)) {
      try {
        window.localStorage.setItem(`gallery-access-${evt.id}`, trimmed);
      } catch {
        // ignore
      }
      setStoredKey(trimmed);
      setKeyInput('');
      setUnlockError('');
    } else {
      setUnlockError('Clé incorrecte, veuillez réessayer.');
    }
  };

  return (
    <div className='min-h-screen font-[family-name:var(--font-geist-sans)]'>
      <div className='w-full'>
        {/* Hero */}
        <div className='relative h-[55vh] overflow-hidden'>
          <Image
            src={evt.cover}
            alt={evt.title}
            width={1200}
            height={500}
            sizes="100vw"
            loading="eager"
            fetchPriority="high"
            className={`w-full h-full object-cover transition-opacity ${hasAccess ? 'opacity-100' : 'opacity-0'}`}
          />
          {isPrivate && !hasAccess && (
            <div className='absolute inset-0 flex items-center justify-center bg-gradient-to-br from-black/60 to-black/40'>
              <div className='rounded-3xl border border-white/30 w-48 h-48 flex items-center justify-center'>
                <LockIcon className='w-12 h-12 text-white' />
              </div>
            </div>
          )}
          <div className='absolute inset-0 bg-black/40' />
          <div className='absolute inset-0 flex items-end'>
            <div className='w-full max-w-5xl mx-auto px-4 pb-10'>
              <Breadcrumb className='mb-4'>
                <BreadcrumbList className='text-white/80'>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link
                        href={getLocalizedPathname(locale, '/')}
                        className='text-white/80 hover:text-white'
                      >
                        Home
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className='text-white/60' />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link
                        href={getLocalizedPathname(locale, '/gallery')}
                        className='text-white/80 hover:text-white'
                      >
                        Galerie
                      </Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className='text-white/60' />
                  <BreadcrumbItem>
                    <BreadcrumbPage className='text-white'>{evt.title}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>

              <div className='flex items-center gap-3 mb-3'>
                <Badge className={isPrivate ? 'bg-rose-200 text-black' : 'bg-[#BBD8E6] text-black'}>
                  {isPrivate ? 'Privé' : 'Public'}
                </Badge>
                <span className='text-white/80 text-sm inline-flex items-center gap-2'>
                  <CalendarIcon className='w-4 h-4' />
                  {new Date(evt.date).toLocaleDateString('fr-FR')}
                </span>
              </div>

              <h1 className='text-4xl md:text-5xl font-bold text-white leading-tight'>
                {evt.title}
              </h1>
              <p className='text-lg text-white/90 mt-3 max-w-3xl'>{evt.description}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className='max-w-5xl mx-auto px-4 py-12'>
          {isPrivate && !hasAccess ? (
            <div className='bg-white dark:bg-neutral-900 rounded-2xl p-6 shadow-lg border border-neutral-200 dark:border-neutral-800'>
              <h3 className='text-xl font-semibold mb-4 flex items-center gap-2'>
                <LockIcon className='w-5 h-5' /> Événement privé
              </h3>
              <p className='text-neutral-600 dark:text-neutral-400 mb-4'>
                Cet événement est protégé. Entrez la clé d’accès pour afficher la galerie.
              </p>
              <form onSubmit={submitKey} className='flex flex-col sm:flex-row gap-2'>
                <Input
                  aria-label='Clé privée'
                  placeholder='Clé d’accès'
                  value={keyInput}
                  onChange={(e) => {
                    setKeyInput(e.target.value);
                    setUnlockError('');
                  }}
                  className='flex-1'
                />
                <Button type='submit'>Déverrouiller</Button>
              </form>
              {unlockError && (
                <p className='text-sm text-red-500 mt-2'>{unlockError}</p>
              )}
              <p className='text-xs text-neutral-500 mt-3'>
                Astuce&nbsp;: une fois validée, la clé reste enregistrée dans votre navigateur.
              </p>
              <div className='mt-4'>
                <Link href={getLocalizedPathname(locale, '/gallery')}>
                  <Button variant='ghost'>← Retour à la galerie</Button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className='mb-10'>
                <h2 className='text-2xl font-bold text-black dark:text-white mb-4 flex items-center gap-2'>
                  <ImageIcon className='w-5 h-5' /> Galerie
                </h2>

                {images.length === 0 ? (
                  <p className='text-neutral-600 dark:text-neutral-400'>
                    Aucune image pour le moment.
                  </p>
                ) : (
                  <EventLightbox title={evt.title} images={images} />
                )}
              </div>

              <Link href={getLocalizedPathname(locale, '/gallery')}>
                <Button variant='outline'>← Retour à la galerie</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function extractPhotos(evt: GalleryDoc): GalleryContentItem[] {
  if (Array.isArray(evt.content) && evt.content.length > 0) {
    return evt.content.map((item, idx) => ({
      ...item,
      id: item.id ?? `content-${idx}`,
    }));
  }

  const legacyCards = Array.isArray(evt.cards) ? evt.cards : [];
  if (legacyCards.length > 0) {
    return legacyCards.map((card, idx) => ({
      id: card.id ?? `card-${idx}`,
      image: card.image,
      title: card.title,
      description: card.description,
    }));
  }

  const legacyMedia = Array.isArray(evt.media) ? evt.media : [];
  if (legacyMedia.length > 0) {
    return legacyMedia
      .filter((m) => m.type === 'image')
      .map((m, idx) => ({
        id: m.id ?? `media-${idx}`,
        image: m.url,
        title: m.title,
        description: m.desc,
      }));
  }

  return [];
}
